/**
 * facetOffer — which values of each facet group the search panel offers, and in what order (plan
 * `2026_11_01_wall_search_panel.md`). The server ranks a group's values for the visitor and the day; the panel takes
 * the first four that the query and the lean do not already hold. Pure: the date is passed in.
 *
 * A group's order: the visitor's own weight for the value (`profile.tokenWeights`), then, in a season group, the
 * current season, the next, the rest (northern calendar), then the share of the house's packs. A value the visitor
 * dropped from their lean ranks last. One value never touched, rotated by the day, is kept apart as the group's
 * discovery and takes its last slot.
 */
import {facetToken, type FacetDef, type HouseFacets} from './facets'

/** Values a group ships for the panel to draw from, discovery apart: enough to fill four after exclusions. */
export const OFFER_RANKED = 8
/** Chips a group shows. */
export const GROUP_VALUES = 4

export type GroupOffer = {
    /** Value ids, best first, the discovery not among them. */
    ranked: string[]
    /** A value the visitor has never touched, or `null` when the group has none to spare. */
    discovery: string | null
}

/** Per facet key. */
export type FacetOffer = Record<string, GroupOffer>

const SEASONS = ['winter', 'spring', 'summer', 'autumn'] as const
const DAY_MS = 86_400_000

/** The northern-hemisphere season of `date` (UTC): December to February winter, then three months each. */
function seasonAt(date: Date): number {
    return Math.floor(((date.getUTCMonth() + 1) % 12) / 3)
}

/** 0 for the current season, 1 for the next, then the rest in turn; a value that is no season goes after them. */
function seasonRank(id: string, date: Date): number {
    const at = SEASONS.indexOf(id as (typeof SEASONS)[number])
    return at === -1 ? SEASONS.length : (at - seasonAt(date) + SEASONS.length) % SEASONS.length
}

/** The group's values by share, most packs first, the `none` value left out: the order of a visitor with no touches
 *  on a day-less read. */
export function shareOrder(def: FacetDef, shares: Record<string, number>): string[] {
    return def.values
        .filter((v) => v.id !== 'none')
        .map((v, at) => ({id: v.id, at, share: shares[facetToken(def.key, v.id)] ?? 0}))
        .sort((a, b) => b.share - a.share || a.at - b.at)
        .map((v) => v.id)
}

/** A small stable number out of a facet key, so two groups do not rotate in step. */
function keyHash(key: string): number {
    let h = 0
    for (const ch of key) h = (h * 31 + ch.charCodeAt(0)) % 9973
    return h
}

export function facetOffer(input: {
    facets: HouseFacets
    /** `profile.tokenWeights` of the session's touches; empty for a newcomer. */
    weights: ReadonlyMap<string, number>
    /** The lean tokens the visitor dropped. */
    suppressed: readonly string[]
    /** The house's `facetShares.json`. */
    shares: Record<string, number>
    date: Date
}): FacetOffer {
    const {facets, weights, suppressed, shares, date} = input
    const day = Math.floor(date.getTime() / DAY_MS)
    const offer: FacetOffer = {}
    for (const def of facets as readonly FacetDef[]) {
        if (def.level === 'tile') continue
        const weightOf = (id: string) => weights.get(facetToken(def.key, id)) ?? 0
        const dropped = (id: string) => suppressed.includes(facetToken(def.key, id))
        const rank = (id: string) => (def.key === 'season' ? seasonRank(id, date) : 0)
        const byShare = shareOrder(def, shares)
        const order = [...byShare].sort(
            (a, b) =>
                Number(dropped(a)) - Number(dropped(b)) ||
                weightOf(b) - weightOf(a) ||
                rank(a) - rank(b) ||
                byShare.indexOf(a) - byShare.indexOf(b),
        )
        // Untouched values past the ones the panel always shows: a value already in the top slots is no discovery.
        const candidates = order.slice(GROUP_VALUES - 1).filter((id) => weightOf(id) <= 0 && !dropped(id))
        const discovery = candidates.length > 0 ? candidates[(day + keyHash(def.key)) % candidates.length] : null
        const ranked = order.filter((id) => id !== discovery).slice(0, OFFER_RANKED)
        if (ranked.length > 0) offer[def.key] = {ranked, discovery}
    }
    return offer
}

/**
 * The values a group shows: the first `GROUP_VALUES` of its offer that `taken` (tokens in the query or the lean) does
 * not hold, the discovery keeping the last slot when it is free. Without an offer for the group, its share order.
 */
export function groupValues(
    def: FacetDef, offer: GroupOffer | undefined, shares: Record<string, number>, taken: readonly string[],
): string[] {
    const free = (id: string) => !taken.includes(facetToken(def.key, id))
    const ranked = (offer?.ranked ?? shareOrder(def, shares)).filter(free)
    const discovery = offer?.discovery && free(offer.discovery) ? offer.discovery : null
    return discovery ? [...ranked.slice(0, GROUP_VALUES - 1), discovery] : ranked.slice(0, GROUP_VALUES)
}
